// src/App.js
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ListView from './components/ListView';
import CatalogView from './components/CatalogView';
import LoginScreen from './components/LoginScreen';
import Toast from './components/Toast';
import { useStores } from './hooks/useStores';
import { AuthProvider, useAuth } from './contexts/AuthContext';

function AuthenticatedApp({ currentView, showToast }) {
  const { t } = useTranslation();
  const {
    lists,
    activeList,
    activeListId,
    updateActiveList,
    addList,
    updateList,
    deleteList,
    fetchLists,
  } = useStores();

  const handleListChange = useCallback((list) => {
    updateActiveList(list);
    showToast(t('toasts.switchedTo', { name: list.name }), 'success');
  }, [updateActiveList, showToast, t]);

  const handleNewList = useCallback(async (newListName) => {
    const added = await addList(newListName);
    if (added) {
      showToast(t('toasts.newListCreated', { name: newListName }), 'success');
    } else {
      showToast(t('toasts.listExists', { name: newListName }), 'warning');
    }
  }, [addList, showToast, t]);

  if (currentView === 'list') {
    return (
      <ListView
        showToast={showToast}
        lists={lists}
        activeList={activeList}
        activeListId={activeListId}
        onListChange={handleListChange}
        onNewList={handleNewList}
        onRenameList={updateList}
        onDeleteList={deleteList}
        onRefreshLists={fetchLists}
      />
    );
  }

  return (
    <CatalogView
      showToast={showToast}
      lists={lists}
      activeList={activeList}
      activeListId={activeListId}
      onListChange={handleListChange}
      onRefreshLists={fetchLists}
    />
  );
}

function AppContent() {
  const { t } = useTranslation();
  const [currentView, setCurrentView] = useState(() => {
    const hash = window.location.hash;
    if (hash === '#catalog') return 'catalog';
    return 'list';
  });

  const [toast, setToast] = useState({
    show: false,
    message: '',
    type: 'success'
  });

  const { isLoading, isAuthenticated, clearSession } = useAuth();

  const showToast = useCallback((message, type = 'success') => {
    setToast({ show: true, message, type });
  }, []);

  const hideToast = useCallback(() => {
    setToast(prev => ({ ...prev, show: false }));
  }, []);

  useEffect(() => {
    const handleUnauthorized = () => {
      clearSession();
      showToast(t('auth.sessionExpired'), 'error');
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [clearSession, showToast, t]);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash === '#list') {
        setCurrentView('list');
      } else if (hash === '#catalog') {
        setCurrentView('catalog');
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-gray-600 font-medium">{t('common.loading')}</div>
        </div>
      );
    }

    if (!isAuthenticated) {
      return <LoginScreen />;
    }

    return (
      <AuthenticatedApp
        currentView={currentView}
        showToast={showToast}
      />
    );
  };

  return (
    <div className="App">
      {renderBody()}

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
